import React, {
	PointerEvent,
	useContext,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
} from 'react';
import useTypedSelector from '../../hooks/use-type-selectors';
import { StageShape } from '../../models/Stage';
import {
	IRotatedRect,
	getAxisAlignedBoundingSize,
	inverseLerp,
	isInsideRotatedRect,
} from '../../utils/geometry';
import {
	clipRoundedRotatedRect,
	drawRotatedRectImage,
	fillRotatedRect,
	fillRotatedRectOval,
	fillRotatedRectTriangle,
	strokeFillRoundedRotatedRect,
	strokeRotatedRect,
} from '../../utils/canvasDrawing';
import { SetPieceColor, SetPieceShape } from '../../models/SetPieces';
import useActions from '../../hooks/use-actions';
import { IDancerPathNode, dancerDefaultSpeed, getDancerPathColor } from '../../models/Timeline';
import { IVec2, Vec2 } from '../../utils/vec2';
import { ImagesContext } from '../../context/ImagesContext';

const audienceSize = { x: 50, y: 50 };
const dancerPathNodeSize = { x: 20, y: 20 };
const handlePointerRadius = 20;
const handleNoMoveRadius = 5;

enum HandleType {
	Move,
	Scale,
	Rotate,
}

interface MoveHandle {
	type: HandleType.Move;
}

interface ScaleHandle {
	type: HandleType.Scale;
	anchor: {
		x: 1 | 0 | -1;
		y: 1 | 0 | -1;
	};
}

interface RotateHandle {
	type: HandleType.Rotate;
}

type Handle = MoveHandle | ScaleHandle | RotateHandle;

const visibleHandles: Handle[] = [
	{ type: HandleType.Scale, anchor: { x: 1, y: 0 } },
	{ type: HandleType.Scale, anchor: { x: 1, y: 1 } },
	{ type: HandleType.Scale, anchor: { x: 0, y: 1 } },
	{ type: HandleType.Scale, anchor: { x: -1, y: 1 } },
	{ type: HandleType.Scale, anchor: { x: -1, y: 0 } },
	{ type: HandleType.Scale, anchor: { x: -1, y: -1 } },
	{ type: HandleType.Scale, anchor: { x: 0, y: -1 } },
	{ type: HandleType.Scale, anchor: { x: 1, y: -1 } },
	{ type: HandleType.Rotate },
];

const calcHandlePos = (
	handle: Handle,
	selection: IRotatedRect,
	canvasScale: number
): IVec2 | null => {
	switch (handle.type) {
		case HandleType.Move: {
			return null;
		}
		case HandleType.Scale: {
			const centerOffset = Vec2.rotate(
				{
					x: selection.size.x * 0.5 * handle.anchor.x,
					y: selection.size.y * 0.5 * handle.anchor.y,
				},
				selection.rotation
			);
			return {
				x: selection.center.x + centerOffset.x,
				y: selection.center.y + centerOffset.y,
			};
		}
		case HandleType.Rotate: {
			const centerOffset = Vec2.rotate(
				{
					x: 0,
					y: selection.size.y * 0.5 + Math.sign(selection.size.y) * (40 * canvasScale),
				},
				selection.rotation
			);
			return {
				x: selection.center.x + centerOffset.x,
				y: selection.center.y + centerOffset.y,
			};
		}
	}
};

interface ISelectionRectSettings {
	canMove: boolean;
	canScale: boolean;
	canRotate: boolean;
}

enum DragType {
	Selection,
	DancerPathNode,
}

interface ISelectionDragInfo {
	type: DragType.Selection;
	handle: Handle;
	startPointerPos: IVec2;
	selectionRect: IRotatedRect;
}

interface IDancerPathNodeDragInfo {
	type: DragType.DancerPathNode;
	dancerId: number;
	pathNodeId: number;
	startPointerPos: IVec2;
	hasPointerMoved: boolean;
	pointerOffset: IVec2;
	pos: IVec2;
}

type IDragInfo = ISelectionDragInfo | IDancerPathNodeDragInfo;

export enum StageCanvasMode {
	ViewOnly,
	Stage,
	DancerPaths,
	SetPiecesAndAudience,
}

interface StageCanvasProps {
	mode: StageCanvasMode;
}

const StageCanvas: React.FC<StageCanvasProps> = ({ mode }: StageCanvasProps) => {
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const [canvasClientSize, setCanvasClientSize] = useState<IVec2>({ x: 0, y: 0 });

	const { images, getPathNodeImage, getTintedImage } = useContext(ImagesContext);

	const stage = useTypedSelector((state) => state.undoable.stage);
	const setPieces = useTypedSelector((state) => state.undoable.setPieces);
	const timeline = useTypedSelector((state) => state.undoable.timeline);
	const settings = useTypedSelector((state) => state.settings);
	const selectedDancer =
		timeline.dancers.find((dancer) => dancer.id === timeline.selectedDancerId) || null;
	const audioPlayback = useTypedSelector((state) => state.audioPlayback);
	const {
		setSetPieceRect,
		setAudiencePosRotation,
		setSelectedSetPiece,
		setStageSize,
		addDancerPathNode,
		setDancerPathNode,
	} = useActions();

	const [drag, setDrag] = useState<IDragInfo | null>(null);
	const [pathNodeInsertIdx, setPathNodeInsertIdx] = useState<number | null>(null);
	const [lastCursorWorldPos, setLastCursorWorldPos] = useState<IVec2 | null>(null);
	const [canvasScale, setCanvasScale] = useState<number | null>(null); // Scale from canvas to world

	let selectionRect: IRotatedRect | null = null; // The selection before being dragged
	let selectionRectSettings: ISelectionRectSettings = {
		canMove: false,
		canScale: false,
		canRotate: false,
	};

	switch (mode) {
		case StageCanvasMode.ViewOnly: {
			selectionRectSettings = { canMove: false, canScale: false, canRotate: false };
			break;
		}
		case StageCanvasMode.Stage: {
			selectionRectSettings = { canMove: false, canScale: true, canRotate: false };
			selectionRect = { center: { x: 0, y: 0 }, size: stage.size, rotation: 0 };
			break;
		}
		case StageCanvasMode.SetPiecesAndAudience: {
			if (setPieces.selectedSetPieceId === 'audience') {
				selectionRectSettings = { canMove: true, canScale: false, canRotate: true };
				selectionRect = {
					center: setPieces.audience.pos,
					size: audienceSize,
					rotation: setPieces.audience.rotation,
				};
			} else {
				selectionRectSettings = { canMove: true, canScale: true, canRotate: true };
				const selectedSetPiece = setPieces.setPieces.find(
					(setPiece) => setPiece.id === setPieces.selectedSetPieceId
				);
				if (selectedSetPiece) {
					selectionRect = {
						center: selectedSetPiece.pos,
						size: selectedSetPiece.size,
						rotation: selectedSetPiece.rotation,
					};
				}
			}
			break;
		}
		case StageCanvasMode.DancerPaths: {
			selectionRectSettings = { canMove: false, canScale: false, canRotate: false };
			if (
				pathNodeInsertIdx === null &&
				selectedDancer !== null &&
				selectedDancer.pathNodes.length === 0
			) {
				setPathNodeInsertIdx(Infinity); // Insert at end of pathNodes
			}
			break;
		}
	}

	// Check for canvas size changes so that the canvas can be re-rendered
	useLayoutEffect(() => {
		const canvas = canvasRef.current;
		if (
			canvas !== null &&
			(canvas.clientWidth !== canvasClientSize.x ||
				canvas.clientHeight !== canvasClientSize.y)
		) {
			setCanvasClientSize({ x: canvas.clientWidth, y: canvas.clientHeight });
		}
	}, [
		canvasClientSize.x,
		canvasClientSize.y,
		canvasRef.current?.clientWidth,
		canvasRef.current?.clientHeight,
	]);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			if (!settings.shortcutsEnabled) return;

			if (event.key === 'Escape') {
				if (mode === StageCanvasMode.DancerPaths) {
					setPathNodeInsertIdx(null);
				}
			}
		};
		document.addEventListener('keydown', onKeyDown);
		return () => document.removeEventListener('keydown', onKeyDown);
	}, [setPathNodeInsertIdx, mode, settings]);

	const stageViewSize = useMemo(() => {
		let bounds: IVec2 = { x: stage.size.x * 0.5, y: stage.size.y * 0.5 };

		bounds.x = Math.max(bounds.x, Math.abs(setPieces.audience.pos.x) + audienceSize.x * 0.5);
		bounds.y = Math.max(bounds.y, Math.abs(setPieces.audience.pos.y) + audienceSize.y * 0.5);
		for (const setPiece of setPieces.setPieces) {
			const boundingSize = getAxisAlignedBoundingSize({
				center: setPiece.pos,
				size: setPiece.size,
				rotation: setPiece.rotation,
			});
			bounds.x = Math.max(bounds.x, Math.abs(setPiece.pos.x) + boundingSize.x * 0.5);
			bounds.y = Math.max(bounds.y, Math.abs(setPiece.pos.y) + boundingSize.y * 0.5);
		}

		for (const dancer of timeline.dancers) {
			for (const pathNode of dancer.pathNodes) {
				bounds.x = Math.max(bounds.x, Math.abs(pathNode.pos.x));
				bounds.y = Math.max(bounds.y, Math.abs(pathNode.pos.y));
			}
		}

		return { x: bounds.x * 2, y: bounds.y * 2 };
	}, [timeline, setPieces, stage]);

	// Render canvas
	useEffect(() => {
		const canvas = canvasRef.current;
		if (canvas === null) return;

		const ctx = canvas.getContext('2d');
		if (ctx === null) return;

		canvas.width = canvasClientSize.x * window.devicePixelRatio;
		canvas.height = canvasClientSize.y * window.devicePixelRatio;
		if (canvas.width === 0 || canvas.height === 0) return;

		let canvasScale;
		const padding = 100;
		if (
			(canvasClientSize.x - padding) / (canvasClientSize.y - padding) >
			stageViewSize.x / stageViewSize.y
		) {
			// Y is the limiting axis
			canvasScale = stageViewSize.y / (canvasClientSize.y - padding);
		} else {
			// X is the limiting axis
			canvasScale = stageViewSize.x / (canvasClientSize.x - padding);
		}
		setCanvasScale(canvasScale);

		// Set background colour
		ctx.fillStyle = '#7e717e';
		ctx.fillRect(0, 0, canvas.width, canvas.height);

		ctx.save();
		{
			// Put (0, 0) at the center
			ctx.translate(canvas.width * 0.5, canvas.height * 0.5);

			// Zoom in so the stage takes up most of the canvas
			// Flip y because in the world forward is positive y, right is positive x
			// and up is positive z forming a right handed coordinate system
			ctx.scale(
				(1 / canvasScale) * window.devicePixelRatio,
				(-1 / canvasScale) * window.devicePixelRatio
			);

			// Draw stage
			ctx.fillStyle = '#060006';
			ctx.strokeStyle = 'white';
			ctx.lineWidth = 3;
			ctx.shadowColor = 'rgba(0, 0, 0, 0.5)';
			ctx.shadowOffsetY = 5;
			ctx.shadowBlur = 10;
			const stageDrawSize =
				mode === StageCanvasMode.Stage && drag?.type === DragType.Selection
					? drag.selectionRect.size
					: stage.size;
			const stageCornerRadius = 10;
			switch (stage.shape) {
				case StageShape.Rectangle: {
					strokeFillRoundedRotatedRect(
						ctx,
						{
							center: { x: 0, y: 0 },
							size: stageDrawSize,
							rotation: 0,
						},
						stageCornerRadius
					);
					break;
				}
				case StageShape.Oval: {
					ctx.beginPath();
					ctx.ellipse(0, 0, stage.size.x * 0.5, stage.size.y * 0.5, 0, 0, Math.PI * 2);
					ctx.fill();
					ctx.stroke();
					break;
				}
			}
			ctx.shadowColor = 'rgba(0, 0, 0, 0)';

			function clipToStage(ctx: CanvasRenderingContext2D) {
				switch (stage.shape) {
					case StageShape.Rectangle: {
						clipRoundedRotatedRect(
							ctx,
							{
								center: { x: 0, y: 0 },
								size: stageDrawSize,
								rotation: 0,
							},
							stageCornerRadius
						);
						break;
					}
					case StageShape.Oval: {
						ctx.beginPath();
						ctx.ellipse(
							0,
							0,
							stage.size.x * 0.5,
							stage.size.y * 0.5,
							0,
							0,
							Math.PI * 2
						);
						ctx.clip();
						break;
					}
				}
			}

			// Setup SetPiece and Audience shadows
			ctx.shadowOffsetX = 0;
			ctx.shadowOffsetY = 0;
			ctx.shadowBlur = 12;
			const onStageShadowColor = 'rgba(255, 255, 255, 0.4)';
			const offStageShadowColor = 'rgba(0, 0, 0, 0.5)';

			// Draw SetPieces
			let setPiecesToDraw = [...setPieces.setPieces];

			// Draw seleceted set piece last so it appears on top
			let selectedSetPieceIndex = setPiecesToDraw.findIndex(
				(setPiece) => setPiece.id === setPieces.selectedSetPieceId
			);

			if (selectedSetPieceIndex >= 0) {
				let selectedSetPiece = setPiecesToDraw.splice(selectedSetPieceIndex, 1)[0];
				setPiecesToDraw.push(selectedSetPiece);
			}

			for (const setPiece of setPiecesToDraw) {
				const setPieceRect =
					mode === StageCanvasMode.SetPiecesAndAudience &&
					setPieces.selectedSetPieceId === setPiece.id &&
					drag?.type === DragType.Selection
						? drag.selectionRect
						: {
								center: setPiece.pos,
								size: setPiece.size,
								rotation: setPiece.rotation,
						  };

				// TODO: Move this out of this file so it can be used for the color dropdown
				switch (setPiece.color) {
					case SetPieceColor.Maroon:
						ctx.fillStyle = 'rgb(147, 42, 42)';
						break;
					case SetPieceColor.Brown:
						ctx.fillStyle = 'rgb(205, 90, 58)';
						break;
					case SetPieceColor.Teal:
						ctx.fillStyle = 'rgb(55, 188, 181)';
						break;
					case SetPieceColor.Navy:
						ctx.fillStyle = 'rgb(38, 107, 150)';
						break;
					case SetPieceColor.Green:
						ctx.fillStyle = 'rgb(57, 170, 97)';
						break;
					case SetPieceColor.Purple:
						ctx.fillStyle = 'rgb(115, 75, 239)';
						break;
					case SetPieceColor.Grey:
						ctx.fillStyle = 'rgb(188, 188, 188)';
						break;
				}

				function drawSetPiece(ctx: CanvasRenderingContext2D) {
					switch (setPiece.shape) {
						case SetPieceShape.Rectangle: {
							fillRotatedRect(ctx, setPieceRect);
							break;
						}
						case SetPieceShape.Oval: {
							fillRotatedRectOval(ctx, setPieceRect);
							break;
						}
						case SetPieceShape.Triangle: {
							fillRotatedRectTriangle(ctx, setPieceRect);
							break;
						}
					}
				}

				ctx.shadowColor = offStageShadowColor;
				drawSetPiece(ctx);

				ctx.shadowColor = onStageShadowColor;
				ctx.save();
				clipToStage(ctx);
				drawSetPiece(ctx);
				ctx.restore();
			}

			// Draw Audience
			if (images.audience !== null) {
				ctx.fillStyle = 'white';
				const audienceRect =
					mode === StageCanvasMode.SetPiecesAndAudience &&
					setPieces.selectedSetPieceId === 'audience' &&
					drag?.type === DragType.Selection
						? drag.selectionRect
						: {
								center: setPieces.audience.pos,
								size: audienceSize,
								rotation: setPieces.audience.rotation,
						  };

				const audienceImage = images.audience;
				function drawAudience(ctx: CanvasRenderingContext2D) {
					ctx.shadowOffsetY = 5;
					ctx.shadowBlur = 12;
					drawRotatedRectImage(ctx, audienceRect, audienceImage);

					ctx.save();
					ctx.scale(1, -1);
					ctx.font = 'bold 12px Sans-Serif';
					const text = 'Audience';
					const textMerics = ctx.measureText(text);
					ctx.fillText(
						text,
						audienceRect.center.x - textMerics.width * 0.5,
						-audienceRect.center.y - audienceRect.size.y * 0.5 - 10
					);
					ctx.restore();
				}

				ctx.shadowColor = offStageShadowColor;
				drawAudience(ctx);

				ctx.shadowColor = onStageShadowColor;
				ctx.save();
				clipToStage(ctx);
				drawAudience(ctx);
				ctx.restore();
			}

			ctx.shadowColor = 'rgba(0, 0, 0, 0)';

			// Draw Dancer Paths
			for (const dancer of timeline.dancers) {
				let pathColor = 'gray';
				if (
					mode !== StageCanvasMode.DancerPaths ||
					dancer.id === timeline.selectedDancerId
				) {
					pathColor = getDancerPathColor(dancer.pathColor);
				}

				// Add an extra path node to draw if we are in the process of adding new nodes
				let pathNodes = dancer.pathNodes;
				if (drag?.type === DragType.DancerPathNode && drag.dancerId === dancer.id) {
					pathNodes = pathNodes.map((pathNode) => {
						if (pathNode.id !== drag.pathNodeId) return pathNode;
						return {
							...pathNode,
							pos: drag.pos,
						};
					});
				}

				if (
					mode === StageCanvasMode.DancerPaths &&
					dancer.id === timeline.selectedDancerId &&
					pathNodeInsertIdx !== null &&
					lastCursorWorldPos !== null
				) {
					pathNodes = [...pathNodes];
					if (pathNodes.length === 0 || pathNodeInsertIdx <= 0) {
						pathNodes.splice(pathNodeInsertIdx, 0, {
							id: -1,
							name: '',
							time: 0,
							delay: 0,
							pos: lastCursorWorldPos,
						});
					} else if (pathNodeInsertIdx >= pathNodes.length) {
						pathNodes.push({
							id: -1,
							name: '',
							time: pathNodes[pathNodes.length - 1].time + 1,
							delay: 0,
							pos: lastCursorWorldPos,
						});
					} else {
						pathNodes.splice(pathNodeInsertIdx, 0, {
							id: -1,
							name: '',
							time:
								(pathNodes[pathNodeInsertIdx - 1].time +
									pathNodes[pathNodeInsertIdx].time) *
								0.5,
							delay: 0,
							pos: lastCursorWorldPos,
						});
					}
				}

				if (pathNodes.length === 0) continue;

				// Draw lines between nodes
				for (let i = 1; i < pathNodes.length; i++) {
					const prevNode = pathNodes[i - 1];
					const curNode = pathNodes[i];

					// Draw thicker line for where dancer has been, and thinner line for dancer's
					// future path based on current timeline time
					ctx.strokeStyle = pathColor;
					ctx.lineWidth = audioPlayback.timelineTime > curNode.time ? 4 : 1;
					ctx.beginPath();
					ctx.moveTo(prevNode.pos.x, prevNode.pos.y);
					ctx.lineTo(curNode.pos.x, curNode.pos.y);
					ctx.stroke();

					if (
						audioPlayback.timelineTime < prevNode.time + prevNode.delay ||
						audioPlayback.timelineTime > curNode.time
					) {
						continue;
					}

					// Dancer is currently between the prevNode and curNode
					const t = inverseLerp(
						prevNode.time + prevNode.delay,
						curNode.time,
						audioPlayback.timelineTime
					);
					const playbackPos = Vec2.lerp(prevNode.pos, curNode.pos, t);

					ctx.lineWidth = 4;
					ctx.beginPath();
					ctx.moveTo(prevNode.pos.x, prevNode.pos.y);
					ctx.lineTo(playbackPos.x, playbackPos.y);
					ctx.stroke();
				}

				function drawPathNodeOutline(
					ctx: CanvasRenderingContext2D,
					pathNode: IDancerPathNode
				) {
					ctx.strokeStyle = 'white';
					ctx.lineWidth = 1;
					strokeRotatedRect(ctx, {
						center: pathNode.pos,
						rotation: 0,
						size: { x: dancerPathNodeSize.x + 5, y: dancerPathNodeSize.y + 5 },
					});
				}

				// Draw first node
				const tintedFirstNodeImage = getTintedImage(images.alphaNode, pathColor);
				if (tintedFirstNodeImage !== null) {
					const firstPathNode = pathNodes[0];
					drawRotatedRectImage(
						ctx,
						{ center: firstPathNode.pos, rotation: 0, size: dancerPathNodeSize },
						tintedFirstNodeImage.canvas
					);
					if (
						timeline.selectedDancerPathNode?.dancerId === dancer.id &&
						timeline.selectedDancerPathNode?.pathNodeId === firstPathNode.id
					) {
						drawPathNodeOutline(ctx, firstPathNode);
					}
				}

				// Draw middle nodes
				if (pathNodes.length > 2) {
					// Get the white node image based on the shape
					const tintedNodeImage = getTintedImage(
						getPathNodeImage(dancer.pathNodeShape),
						pathColor
					);
					if (tintedNodeImage !== null) {
						for (let i = 1; i < pathNodes.length - 1; i++) {
							const pathNode = pathNodes[i];
							drawRotatedRectImage(
								ctx,
								{ center: pathNode.pos, rotation: 0, size: dancerPathNodeSize },
								tintedNodeImage.canvas
							);
							if (
								timeline.selectedDancerPathNode?.dancerId === dancer.id &&
								timeline.selectedDancerPathNode?.pathNodeId === pathNode.id
							) {
								drawPathNodeOutline(ctx, pathNode);
							}
						}
					}
				}

				// Draw last node
				if (pathNodes.length > 1) {
					const tintedLastNodeImage = getTintedImage(images.omegaNode, pathColor);
					if (tintedLastNodeImage) {
						const lastPathNode = pathNodes[pathNodes.length - 1];
						drawRotatedRectImage(
							ctx,
							{ center: lastPathNode.pos, rotation: 0, size: dancerPathNodeSize },
							tintedLastNodeImage.canvas
						);
						if (
							timeline.selectedDancerPathNode?.dancerId === dancer.id &&
							timeline.selectedDancerPathNode?.pathNodeId === lastPathNode.id
						) {
							drawPathNodeOutline(ctx, lastPathNode);
						}
					}
				}
			}

			// Draw selection
			if (selectionRect !== null) {
				const rect = drag?.type === DragType.Selection ? drag.selectionRect : selectionRect;
				const selectionColor = '#ffdd00';
				const selectionActiveColor = 'white';

				ctx.strokeStyle =
					drag?.type === DragType.Selection && drag.handle.type === HandleType.Move
						? selectionActiveColor
						: selectionColor;
				ctx.lineWidth = 2 * canvasScale;
				strokeRotatedRect(ctx, rect);

				if (selectionRectSettings.canRotate) {
					const topCenterHandlePos = calcHandlePos(
						{ type: HandleType.Scale, anchor: { x: 0, y: 1 } },
						rect,
						canvasScale
					);
					const rotationHandlePos = calcHandlePos(
						{ type: HandleType.Rotate },
						rect,
						canvasScale
					);
					if (topCenterHandlePos !== null && rotationHandlePos !== null) {
						ctx.strokeStyle = selectionColor;
						ctx.beginPath();
						ctx.moveTo(topCenterHandlePos.x, topCenterHandlePos.y);
						ctx.lineTo(rotationHandlePos.x, rotationHandlePos.y);
						ctx.stroke();
					}
				}

				const handleSize = { x: 10 * canvasScale, y: 10 * canvasScale };
				for (const handle of visibleHandles) {
					ctx.fillStyle =
						drag?.type === DragType.Selection && drag.handle === handle
							? selectionActiveColor
							: selectionColor;

					const handlePos = calcHandlePos(handle, rect, canvasScale);
					if (handlePos === null) continue;

					if (handle.type === HandleType.Rotate) {
						if (selectionRectSettings.canRotate) {
							ctx.beginPath();
							ctx.arc(handlePos.x, handlePos.y, handleSize.x * 0.5, 0, Math.PI * 2);
							ctx.fill();
						}
					} else {
						if (selectionRectSettings.canScale) {
							fillRotatedRect(ctx, {
								center: handlePos,
								size: handleSize,
								rotation: rect.rotation,
							});
						}
					}
				}
			}
		}
		ctx.restore();
	}, [
		canvasRef,
		images,
		getPathNodeImage,
		getTintedImage,
		canvasClientSize,
		stage,
		setPieces,
		setPieces.setPieces,
		drag,
		mode,
		selectionRect,
		selectionRectSettings.canMove,
		selectionRectSettings.canScale,
		selectionRectSettings.canRotate,
		audioPlayback.timelineTime,
		timeline.dancers,
		timeline.selectedDancerId,
		lastCursorWorldPos,
		pathNodeInsertIdx,
		stageViewSize,
	]);

	const getWorldPointerPos = (
		event: PointerEvent<HTMLCanvasElement>,
		canvasScale: number
	): IVec2 => {
		let canvas = event.currentTarget;
		const rect = canvas.getBoundingClientRect();
		const pointerCanvasPos = { x: event.clientX - rect.left, y: event.clientY - rect.top };
		const worldPointerPos = {
			x: (pointerCanvasPos.x - canvas.clientWidth * 0.5) * canvasScale,
			y: -(pointerCanvasPos.y - canvas.clientHeight * 0.5) * canvasScale,
		};
		return worldPointerPos;
	};

	const onPointerDown = (event: PointerEvent<HTMLCanvasElement>) => {
		event.currentTarget.setPointerCapture(event.pointerId);
		if (canvasScale === null) return;
		const pointerPos = getWorldPointerPos(event, canvasScale);

		switch (mode) {
			case StageCanvasMode.ViewOnly:
				break;
			case StageCanvasMode.Stage: {
				if (selectionRect === null) return;

				let handle = isOverHandle(
					selectionRect,
					selectionRectSettings,
					pointerPos,
					canvasScale
				);
				if (handle === null) return;

				event.currentTarget.style.cursor = 'grabbing';
				setDrag({
					type: DragType.Selection,
					handle: handle,
					startPointerPos: pointerPos,
					selectionRect: selectionRect,
				});
				break;
			}
			case StageCanvasMode.DancerPaths: {
				if (selectedDancer === null) return;

				if (pathNodeInsertIdx !== null) {
					if (selectedDancer.pathNodes.length === 0 || pathNodeInsertIdx <= 0) {
						addDancerPathNode(selectedDancer.id, 0, 0, pointerPos);
						setPathNodeInsertIdx(pathNodeInsertIdx + 1);
					} else if (pathNodeInsertIdx >= selectedDancer.pathNodes.length) {
						const prevDancerPathNode =
							selectedDancer.pathNodes[selectedDancer.pathNodes.length - 1];
						const prevNodeDist = Vec2.distance(prevDancerPathNode.pos, pointerPos);
						if (prevNodeDist < handlePointerRadius * canvasScale) {
							setPathNodeInsertIdx(null);
							return;
						}

						addDancerPathNode(
							selectedDancer.id,
							prevDancerPathNode.time + prevNodeDist / dancerDefaultSpeed,
							0,
							pointerPos
						);
						setPathNodeInsertIdx(pathNodeInsertIdx + 1);
					} else {
						const prevDancerPathNode = selectedDancer.pathNodes[pathNodeInsertIdx - 1];
						const prevNodeDist = Vec2.distance(prevDancerPathNode.pos, pointerPos);
						if (prevNodeDist < handlePointerRadius * canvasScale) {
							setPathNodeInsertIdx(null);
							return;
						}

						const nextDancerPathNode = selectedDancer.pathNodes[pathNodeInsertIdx];
						addDancerPathNode(
							selectedDancer.id,
							(prevDancerPathNode.time + nextDancerPathNode.time) * 0.5,
							0,
							pointerPos
						);
						setPathNodeInsertIdx(pathNodeInsertIdx + 1);
					}
				} else {
					let closestPathNode = null;
					let closestPathNodeDist = Infinity;
					for (const pathNode of selectedDancer.pathNodes) {
						const dist = Vec2.distance(pathNode.pos, pointerPos);
						if (dist < closestPathNodeDist) {
							closestPathNode = pathNode;
							closestPathNodeDist = dist;
						}
					}

					if (
						closestPathNode !== null &&
						closestPathNodeDist < handlePointerRadius * canvasScale
					) {
						setDrag({
							type: DragType.DancerPathNode,
							dancerId: selectedDancer.id,
							pathNodeId: closestPathNode.id,
							startPointerPos: pointerPos,
							pointerOffset: {
								x: closestPathNode.pos.x - pointerPos.x,
								y: closestPathNode.pos.y - pointerPos.y,
							},
							hasPointerMoved: false,
							pos: closestPathNode.pos,
						});
					}
				}
				break;
			}
			case StageCanvasMode.SetPiecesAndAudience: {
				if (selectionRect === null) {
					if (
						isInsideRotatedRect(
							{
								center: setPieces.audience.pos,
								size: audienceSize,
								rotation: setPieces.audience.rotation,
							},
							pointerPos
						)
					) {
						setSelectedSetPiece('audience');
						return;
					}

					// Iterate backwards because the last setPiece appears on top
					for (let i = setPieces.setPieces.length - 1; i >= 0; i--) {
						const setPiece = setPieces.setPieces[i];
						if (
							isInsideRotatedRect(
								{
									center: setPiece.pos,
									size: setPiece.size,
									rotation: setPiece.rotation,
								},
								pointerPos
							)
						) {
							setSelectedSetPiece(setPiece.id);
							return;
						}
					}

					return;
				}

				let handle = isOverHandle(
					selectionRect,
					selectionRectSettings,
					pointerPos,
					canvasScale
				);
				if (handle === null) {
					if (
						isInsideRotatedRect(
							{
								center: setPieces.audience.pos,
								size: audienceSize,
								rotation: setPieces.audience.rotation,
							},
							pointerPos
						)
					) {
						setSelectedSetPiece('audience');
						return;
					}

					// Iterate backwards because the last setPiece appears on top
					for (let i = setPieces.setPieces.length - 1; i >= 0; i--) {
						const setPiece = setPieces.setPieces[i];
						if (
							isInsideRotatedRect(
								{
									center: setPiece.pos,
									size: setPiece.size,
									rotation: setPiece.rotation,
								},
								pointerPos
							)
						) {
							setSelectedSetPiece(setPiece.id);
							return;
						}
					}

					setSelectedSetPiece('audience');
					return;
				}

				event.currentTarget.style.cursor = 'grabbing';
				setDrag({
					type: DragType.Selection,
					handle: handle,
					startPointerPos: pointerPos,
					selectionRect: selectionRect,
				});
				break;
			}
		}
	};

	const onPointerMove = (event: PointerEvent<HTMLCanvasElement>) => {
		if (canvasScale === null) return;
		const pointerPos = getWorldPointerPos(event, canvasScale);
		setLastCursorWorldPos(pointerPos);

		if (selectionRect === null) {
			event.currentTarget.style.cursor = 'auto';
		}

		if (drag === null) {
			if (selectionRect === null) return;
			let handle = isOverHandle(
				selectionRect,
				selectionRectSettings,
				pointerPos,
				canvasScale
			);
			event.currentTarget.style.cursor = handle !== null ? 'grab' : 'auto';
			return;
		}

		switch (drag.type) {
			case DragType.Selection: {
				if (selectionRect === null) return;

				const pointerDelta = {
					x: pointerPos.x - drag.startPointerPos.x,
					y: pointerPos.y - drag.startPointerPos.y,
				};
				event.currentTarget.style.cursor = 'grabbing';

				if (drag.handle.type === HandleType.Move) {
					let newSelectionCenter = {
						x: selectionRect.center.x + pointerDelta.x,
						y: selectionRect.center.y + pointerDelta.y,
					};
					setDrag({
						...drag,
						selectionRect: { ...selectionRect, center: newSelectionCenter },
					});
				} else if (drag.handle.type === HandleType.Rotate) {
					let newAngleRadians = Math.atan2(
						pointerPos.y - selectionRect.center.y,
						pointerPos.x - selectionRect.center.x
					);
					let newRotation = (newAngleRadians * 180) / Math.PI - 90;

					if (selectionRect.size.y < 0) {
						newRotation += 180;
					}

					if (event.shiftKey) {
						newRotation = Math.round(newRotation / 45) * 45;
					}

					setDrag({
						...drag,
						selectionRect: { ...selectionRect, rotation: newRotation },
					});
				} else {
					// Scale

					let rotatedPointerDelta = Vec2.rotate(pointerDelta, -selectionRect.rotation);

					let newSelectionSize = { x: selectionRect.size.x, y: selectionRect.size.y };
					let newSelectionPos = { x: selectionRect.center.x, y: selectionRect.center.y };

					if (selectionRectSettings.canMove && !event.altKey) {
						// Scale and Move
						newSelectionSize = {
							x: selectionRect.size.x + rotatedPointerDelta.x * drag.handle.anchor.x,
							y: selectionRect.size.y + rotatedPointerDelta.y * drag.handle.anchor.y,
						};

						if (event.shiftKey) {
							const scaleRatio = selectionRect.size.x / selectionRect.size.y;
							const newScaleRatio = newSelectionSize.x / newSelectionSize.y;
							if (Math.abs(scaleRatio) > Math.abs(newScaleRatio)) {
								newSelectionSize.x =
									newSelectionSize.y * scaleRatio * Math.sign(newScaleRatio);
							} else {
								newSelectionSize.y =
									(newSelectionSize.x / scaleRatio) * Math.sign(newScaleRatio);
							}
						}

						let newSelectionPosDelta = Vec2.rotate(
							{
								x:
									selectionRect.size.x * 0.5 * -drag.handle.anchor.x +
									newSelectionSize.x * 0.5 * drag.handle.anchor.x,
								y:
									selectionRect.size.y * 0.5 * -drag.handle.anchor.y +
									newSelectionSize.y * 0.5 * drag.handle.anchor.y,
							},
							selectionRect.rotation
						);

						newSelectionPos = {
							x: selectionRect.center.x + newSelectionPosDelta.x,
							y: selectionRect.center.y + newSelectionPosDelta.y,
						};
					} else {
						// Just scale
						newSelectionSize = {
							x:
								selectionRect.size.x +
								rotatedPointerDelta.x * 2 * drag.handle.anchor.x,
							y:
								selectionRect.size.y +
								rotatedPointerDelta.y * 2 * drag.handle.anchor.y,
						};

						if (event.shiftKey) {
							const scaleRatio = selectionRect.size.x / selectionRect.size.y;
							const newScaleRatio = newSelectionSize.x / newSelectionSize.y;
							if (Math.abs(scaleRatio) > Math.abs(newScaleRatio)) {
								newSelectionSize.x =
									newSelectionSize.y * scaleRatio * Math.sign(newScaleRatio);
							} else {
								newSelectionSize.y =
									(newSelectionSize.x / scaleRatio) * Math.sign(newScaleRatio);
							}
						}
					}

					setDrag({
						...drag,
						selectionRect: {
							...selectionRect,
							center: newSelectionPos,
							size: newSelectionSize,
						},
					});
				}
				break;
			}
			case DragType.DancerPathNode: {
				const hasMoved =
					drag.hasPointerMoved ||
					Vec2.distance(drag.startPointerPos, pointerPos) >
						handleNoMoveRadius * canvasScale;
				const newPos = {
					x: pointerPos.x + drag.pointerOffset.x,
					y: pointerPos.y + drag.pointerOffset.y,
				};
				setDrag({ ...drag, pos: newPos, hasPointerMoved: hasMoved });
				break;
			}
		}
	};

	const onPointerUp = (event: PointerEvent<HTMLCanvasElement>) => {
		if (canvasScale === null) return;
		const pointerPos = getWorldPointerPos(event, canvasScale);

		switch (mode) {
			case StageCanvasMode.ViewOnly: {
				break;
			}
			case StageCanvasMode.Stage: {
				if (drag?.type === DragType.Selection) {
					setStageSize({
						x: Math.round(Math.abs(drag.selectionRect.size.x)),
						y: Math.round(Math.abs(drag.selectionRect.size.y)),
					});
				}
				break;
			}
			case StageCanvasMode.DancerPaths: {
				if (drag?.type === DragType.DancerPathNode) {
					const hasMoved =
						drag.hasPointerMoved ||
						Vec2.distance(drag.startPointerPos, pointerPos) >
							handleNoMoveRadius * canvasScale;
					if (hasMoved) {
						const pathNode = timeline.dancers
							.find((dancer) => dancer.id === drag.dancerId)
							?.pathNodes.find((pathNode) => pathNode.id === drag.pathNodeId);
						if (pathNode !== undefined) {
							const newPos = {
								x: pointerPos.x + drag.pointerOffset.x,
								y: pointerPos.y + drag.pointerOffset.y,
							};
							setDancerPathNode(
								drag.dancerId,
								drag.pathNodeId,
								pathNode.name,
								pathNode.time,
								pathNode.delay,
								newPos
							);
						}
					} else {
						const pathNodeIdx = timeline.dancers
							.find((dancer) => dancer.id === drag.dancerId)
							?.pathNodes.findIndex((pathNode) => pathNode.id === drag.pathNodeId);
						if (pathNodeIdx !== undefined && pathNodeIdx >= 0) {
							setPathNodeInsertIdx(pathNodeIdx + 1);
						}
					}
				}
				break;
			}
			case StageCanvasMode.SetPiecesAndAudience: {
				if (drag?.type === DragType.Selection) {
					if (setPieces.selectedSetPieceId === 'audience') {
						setAudiencePosRotation(
							drag.selectionRect.center,
							drag.selectionRect.rotation
						);
					} else if (setPieces.selectedSetPieceId !== null) {
						setSetPieceRect(
							setPieces.selectedSetPieceId,
							drag.selectionRect.center,
							drag.selectionRect.size,
							drag.selectionRect.rotation
						);
					}
				}
				break;
			}
		}

		if (selectionRect !== null) {
			let handle = isOverHandle(
				selectionRect,
				selectionRectSettings,
				pointerPos,
				canvasScale
			);
			event.currentTarget.style.cursor = handle ? 'grab' : 'auto';
		}

		setDrag(null);
	};

	return (
		<canvas
			ref={canvasRef}
			onPointerDown={onPointerDown}
			onPointerMove={onPointerMove}
			onPointerUp={onPointerUp}
			onPointerLeave={() => setLastCursorWorldPos(null)}
			onKeyDownCapture={(event) => {
				console.log(event);
				event.currentTarget.dispatchEvent(new Event('pointermove'));
			}}
			style={{ width: '100%', height: '100%' }}
		></canvas>
	);
};

export default StageCanvas;

function isOverHandle(
	selection: IRotatedRect,
	selectionSettings: ISelectionRectSettings,
	point: IVec2,
	canvasScale: number
): Handle | null {
	if (selection === null || canvasScale === null) return null;

	if (selectionSettings.canScale || selectionSettings.canRotate) {
		const handleWorldRadius = handlePointerRadius * canvasScale;
		const sqrHandleRadius = handleWorldRadius * handleWorldRadius;

		let closestHandle: Handle | null = null;
		let closestHandleSqrDist = Infinity;

		if (selectionSettings.canMove) {
			closestHandle = { type: HandleType.Move };
			closestHandleSqrDist = Vec2.sqrDistance(point, selection.center);
		}

		for (const handle of visibleHandles) {
			if (handle.type === HandleType.Rotate) {
				if (!selectionSettings.canRotate) continue;
			} else if (handle.type === HandleType.Scale) {
				if (!selectionSettings.canScale) continue;
			}

			const handlePos = calcHandlePos(handle, selection, canvasScale);
			if (handlePos === null) continue;

			const sqrDist = Vec2.sqrDistance(point, handlePos);
			if (sqrDist < closestHandleSqrDist) {
				closestHandle = handle;
				closestHandleSqrDist = sqrDist;
			}
		}

		if (closestHandleSqrDist < sqrHandleRadius) {
			return closestHandle;
		}
	}

	if (selectionSettings.canMove) {
		if (isInsideRotatedRect(selection, point)) {
			return { type: HandleType.Move };
		}
	}

	return null;
}
